import { isBannedError, getBannedError } from '@aurora/shared-apollo/FetchError';
import ApolloErrorAlert from '@aurora/shared-client/components/common/ApolloErrorAlert/ApolloErrorAlert';
import { LoadingVariant } from '@aurora/shared-client/components/common/Loading/enums';
import Loading from '@aurora/shared-client/components/common/Loading/Loading';
import type { LoadingVariantTypeAndProps } from '@aurora/shared-client/components/common/Loading/types';
import type { AppContextInterface } from '@aurora/shared-client/components/context/AppContext/AppContext';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import CachedThemeContextProvider from '@aurora/shared-client/components/context/ThemeContext/CachedThemeContextProvider/CachedThemeContextProvider';
import PageError from '@aurora/shared-client/components/error/PageError/PageError';
import useLoginRedirect from '@aurora/shared-client/components/useLoginRedirect';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import type {
  MessagePagesAndParams,
  UserPageAndParams
} from '@aurora/shared-client/routes/endUserRoutes';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import { LoadingSize, LoadingSpacing } from '@aurora/shared-client/types/enums';
import type { PageContext } from '@aurora/shared-generated/types/graphql-schema-types';
import {
  VisibilityScope,
  RegistrationStatus
} from '@aurora/shared-generated/types/graphql-schema-types';
import type { ContextCommunityFragment } from '@aurora/shared-generated/types/graphql-types';
import { LocalStorageKeys } from '@aurora/shared-types/community/enums';
import { EndUserComponent, EndUserPathParams } from '@aurora/shared-types/pages/enums';
import {
  AccessFunctionType,
  RestrictedPageBehavior
} from '@aurora/shared-types/redirects/pageRedirect';
import type { FormatMessageKey, FormatMessageValues } from '@aurora/shared-types/texts';
import { DEFAULT_THEME_ID } from '@aurora/shared-utils/helpers/theme/ThemeConstant';
import UrlBuilder from '@aurora/shared-utils/helpers/urls/UrlHelper/UrlBuilder';
import { getLog } from '@aurora/shared-utils/log';
import { canUseDOM } from 'exenv';
import React, { useCallback, useContext } from 'react';
import type {
  AppContextQuery,
  AppContextQueryVariables,
  AuthUserQuery,
  ContextNodeFragment,
  ContextNodeQuery,
  ContextNodeQueryVariables,
  ContextUserQuery,
  ContextUserQueryVariables
} from '../../../../types/graphql-types';
import useEndUserPageDescriptor from '../../../useEndUserPageDescriptor';
import usePageAccess from '../../../usePageAccess';
import useTranslation from '../../../useTranslation';
import useContextObjectFromUrl, {
  getNodeIdFromUrl,
  UrlObject,
  useContextObjectRefFromUrl
} from '../../useContextObjectFromUrl';
import appContextQuery from './AppContext.query.graphql';
import authUserQuery from '@aurora/shared-client/components/context/AppContext/AppContextProvider/AuthUser.query.graphql';
import useAppContextProviderMessage from './useAppContextProviderMessage';
import RedirectionWrapper from './RedirectionWrapper';

const log = getLog(module);

interface ErrorKey {
  key?: FormatMessageKey;
  values?: FormatMessageValues;
  description?: string;
  statusCode?: number;
}

interface Props {
  /**
   * Is the request from a search crawler?
   */
  isCrawler?: boolean;
}

/**
 * Put aurora-specific data in the Aurora context.
 *
 * @author Dolan Halbrook, Adam Ayres
 */
const AppContextProvider: React.FC<React.PropsWithChildren<Props>> = ({ children, isCrawler }) => {
  const tenant = useContext(TenantContext);
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.APP_CONTEXT_PROVIDER
  );
  const { router } = useEndUserRoutes();
  const {
    privateCommunityAnonAllowed,
    redirectBehavior: {
      pageAccessResolver: { resolverFunctionType },
      restrictedPageBehavior
    }
  } = useEndUserPageDescriptor();

  const skipPolicies: boolean =
    restrictedPageBehavior !== RestrictedPageBehavior.LOCALIZED_CATEGORIES_PAGE &&
    resolverFunctionType !== AccessFunctionType.NOTIFICATIONS_PAGE &&
    resolverFunctionType !== AccessFunctionType.CASE_PORTAL &&
    resolverFunctionType !== AccessFunctionType.INBOX_PAGE &&
    resolverFunctionType !== AccessFunctionType.CREATE_GROUP_HUB_PAGE &&
    resolverFunctionType !== AccessFunctionType.MAINTENANCE_PAGE &&
    resolverFunctionType !== AccessFunctionType.WORKSTREAMS_PAGE;

  // Message from URL
  const { id: messageId } = useContextObjectRefFromUrl(UrlObject.MESSAGE);
  const { id: replyMessageId } = useContextObjectRefFromUrl(UrlObject.REPLY);

  const urlMessageQueryResult = useAppContextProviderMessage(module, replyMessageId);
  const {
    data: urlMessageData,
    loading: urlMessageLoading,
    error: urlMessageError
  } = urlMessageQueryResult;
  const contextMessage = urlMessageData?.message ?? null;
  const messageArchivalData = urlMessageData?.messageArchivalData ?? null;

  // Node from URL
  const urlNodeQueryResult = useContextObjectFromUrl<ContextNodeQuery, ContextNodeQueryVariables>(
    module,
    UrlObject.NODE,
    {
      skip: urlMessageLoading || contextMessage != null
    }
  );
  const { data: urlNodeData, error: urlNodeError, loading: urlNodeLoading } = urlNodeQueryResult;
  const contextNode = urlNodeData?.coreNode ?? null;

  // Aurora context
  const appContextQueryResult = useQueryWithTracing<AppContextQuery, AppContextQueryVariables>(
    module,
    appContextQuery,
    {
      variables: {
        useCommunityPolicies: !skipPolicies
      },
      skip: urlNodeLoading || urlMessageLoading
    }
  );
  const {
    data: appContextData,
    loading: appContextLoading,
    error: appContextError
  } = appContextQueryResult;

  // Auth user
  const {
    data: authUserData,
    loading: authUserLoading,
    error: authUserError
  } = useQueryWithTracing<AuthUserQuery>(module, authUserQuery);

  // User from URL
  const urlUserQueryResult = useContextObjectFromUrl<ContextUserQuery, ContextUserQueryVariables>(
    module,
    UrlObject.USER
  );
  const { data: urlUserData, error: urlUserError, loading: urlUserLoading } = urlUserQueryResult;

  const isLoading =
    (canUseDOM && localStorage?.getItem(LocalStorageKeys.AUTH_INVALIDATING_KEY) != null) ||
    (!appContextData && appContextLoading) ||
    (!authUserData && authUserLoading) ||
    (!urlNodeData && urlNodeLoading) ||
    (!urlMessageData && urlMessageLoading) ||
    (!urlUserData && urlUserLoading) ||
    textLoading;

  const { self } = authUserData ?? {};
  const { canAccess, canRegister } = appContextData ?? {};
  const isAnonymous = self?.registrationData?.status === RegistrationStatus.Anonymous;

  const canAccessCommunity = useCallback(() => {
    if (isLoading) {
      return true;
    }

    if (canAccess === false && isAnonymous === true) {
      return false;
    }

    if (appContextError) {
      /**
       * Check if any of the GraphQL errors are from the user being `UNAUTHENTICATED`.
       * In which case we know the community is set to private and only accessibly by
       * authenticated users.
       */
      return !appContextError.graphQLErrors.some(
        item => item?.extensions?.code === 'ANONYMOUS_ACCESS_NOT_ALLOWED'
      );
    }
    return true;
  }, [canAccess, appContextError, isAnonymous, isLoading]);

  const communityId = appContextData?.community?.id;

  /**
   * When the community is set to private and the user is unauthenticated, then we redirect them to the login page
   */
  const shouldLoginRedirect: boolean = !canAccessCommunity() && !privateCommunityAnonAllowed;
  useLoginRedirect(shouldLoginRedirect);

  const contextNodeQueryResult =
    messageId || replyMessageId
      ? {
          loading: urlMessageLoading,
          data: {
            coreNode: contextMessage?.board
          },
          error: urlMessageError
        }
      : urlNodeQueryResult;

  const pageParamData = {
    appContextQueryResult: appContextQueryResult,
    contextNodeQueryResult,
    contextMessageQueryResult: urlMessageQueryResult,
    contextUserQueryResult: urlUserQueryResult
  };

  /**
   * If the `contextMessage` is present and accessible, it will contain the archival data directly for users with the
   * necessary permissions. If it is null, the data will come from the messageArchivalData query.
   */
  const isContextMessageArchived: boolean = contextMessage
    ? contextMessage?.visibilityScope === VisibilityScope.Archived
    : messageArchivalData?.archived;

  /**
   * Protected node pages redirect the user to the community page with an alert. If the user is anonymous, they are first
   * redirected to the login page
   */
  const { loading: pageAccessLoading } = usePageAccess(
    pageParamData,
    isAnonymous,
    communityId,
    !isContextMessageArchived
  );

  const loadingVariant: LoadingVariantTypeAndProps = {
    type: LoadingVariant.DOT,
    props: {
      size: LoadingSize.LG,
      spacing: LoadingSpacing.XL
    }
  };

  // if in loading state or redirecting to a login screen, bail out
  if (isLoading || shouldLoginRedirect || pageAccessLoading) {
    return <Loading variant={loadingVariant} />;
  }

  const {
    componentsLastModified,
    pagesLastModified,
    pageScriptsLastModified,
    quiltLastModified,
    textLastModified,
    themeLastModified,
    quiltWrapperLastModified,
    entityDefinitionsLastModified,
    fieldDefinitionsLastModified
  } = appContextData ?? {};

  function getCommunity(): ContextCommunityFragment {
    // When the community is set to disallow anon access, and the user is not
    // authenticated, then we provide a stub of a community object
    if (!canAccessCommunity()) {
      return {
        id: null,
        title: null,
        displayId: null,
        nodeType: 'community',
        userContext: { canUpdateNode: false },
        seoProperties: {
          customOGSiteName: '',
          appendTopicUidInHead: false,
          linkCommentToTimeStamp: true,
          lowercasePath: true
        }
      };
    }
    return appContextData?.community ?? null;
  }

  const community = getCommunity();
  const authUser = self ?? null;
  const contextUser = urlUserData?.user ?? null;

  const isUserBanned =
    appContextError?.networkError && isBannedError(appContextError?.networkError);

  function getErrorTextKeyAndValues(): ErrorKey {
    if (isUserBanned) {
      const bannedError = getBannedError(appContextError?.networkError);
      const reason = bannedError.extensions?.relatedFields?.reason?.message;
      return {
        key: 'userBanned',
        description: reason ? formatMessage('userBannedReason', { reason }) : null,
        statusCode: 403
      };
    }

    if (tenant?.enabled === false || !community) {
      return { key: 'noCommunity' };
    }

    if (!authUser) {
      return { key: 'noUser' };
    }

    const messageFromUrl =
      router.getPathParams<MessagePagesAndParams>()[EndUserPathParams.MESSAGE_ID];

    if (!isContextMessageArchived && !contextMessage && messageFromUrl) {
      return { key: 'noMessage', values: { messageId: messageFromUrl } };
    }

    const nodeIdFromUrl = getNodeIdFromUrl(router);

    if (!isContextMessageArchived && !contextNode && nodeIdFromUrl && !messageFromUrl) {
      return { key: 'noNode', values: { nodeId: nodeIdFromUrl } };
    }

    const userFromUrl = router.getPathParams<UserPageAndParams>()[EndUserPathParams.USER_ID];

    if (!contextUser && router.getPathParams<UserPageAndParams>()[EndUserPathParams.USER_ID]) {
      return { key: 'noUser', values: { userId: userFromUrl } };
    }

    return {};
  }

  const pageEntities = [];
  if (contextNode?.id) {
    pageEntities.push(contextNode.id);
  }
  if (contextMessage?.id) {
    pageEntities.push(contextMessage.id);
  }
  if (contextUser?.id) {
    pageEntities.push(contextUser.id);
  }

  const pageTemplateContext: PageContext = {
    name: router.getCurrentPageName(),
    props: {},
    url: UrlBuilder.fromUrl(tenant.baseUrl).addPath(router.getPathWithoutQueryStrings()).build(),
    entities: pageEntities
  };

  if (urlNodeError) {
    log.error(urlNodeError, 'Error fetching node from URL');
  }

  if (urlMessageError) {
    log.error(urlMessageError, 'Error fetching message from URL');
  }

  if (urlUserError) {
    log.error(urlUserError, 'Error fetching user from URL');
  }

  if (authUserError) {
    log.error(authUserError, 'Error fetching auth user');
  }

  if (appContextError) {
    log.error(appContextError, 'Error fetching app context');
  }

  const effectiveContextNode = (contextNode ??
    contextMessage?.board ??
    community) as ContextNodeFragment;

  const context: AppContextInterface = {
    community,
    authUser,
    contextNode: effectiveContextNode,
    contextMessage,
    contextMessageArchivalData: messageArchivalData,
    contextUser,
    componentsLastModified,
    pagesLastModified,
    pageScriptsLastModified,
    quiltLastModified,
    quiltWrapperLastModified,
    textLastModified,
    entityDefinitionsLastModified,
    fieldDefinitionsLastModified,
    themeInfo: {
      id: effectiveContextNode?.theme?.id ?? DEFAULT_THEME_ID,
      lastModified: themeLastModified
    },
    canAccess,
    canRegister,
    isCrawler,
    pageTemplateContext
  };

  log.trace('AppContext is %O', context);

  const {
    key: errorKey,
    values: errorValues,
    description,
    statusCode
  } = getErrorTextKeyAndValues();

  return (
    <AppContext.Provider value={context}>
      <RedirectionWrapper pageParams={pageParamData}>
        {appContextError && <ApolloErrorAlert error={appContextError} />}
        {authUserError && <ApolloErrorAlert error={authUserError} />}
        {urlNodeError && <ApolloErrorAlert error={urlNodeError} />}
        {urlMessageError && <ApolloErrorAlert error={urlMessageError} />}
        {urlUserError && <ApolloErrorAlert error={urlUserError} />}
        {errorKey === undefined ? (
          children
        ) : (
          <CachedThemeContextProvider>
            <PageError
              statusCode={statusCode ?? 404}
              title={formatMessage(errorKey, errorValues)}
              description={description}
              showCommunityLink={!isUserBanned}
            />
          </CachedThemeContextProvider>
        )}
      </RedirectionWrapper>
    </AppContext.Provider>
  );
};

export default AppContextProvider;
